import {SidePanel} from '@github-ui/side-panel'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import type {RefObject} from 'react'

import {ArtifactCopyButton} from './ArtifactCopyButton'
import {ArtifactEditButton} from './ArtifactEditButton'
import {ArtifactHistoryMenu} from './ArtifactHistoryMenu'
import {ArtifactPanelBody, type PanelArtifact, useArtifactVersionSelection} from './ArtifactPanelContent'
import styles from './ArtifactsSidePanelOverlay.module.css'
import {ArtifactsTabBar, type TabItem} from './ArtifactsTabBar'
import type {EditableArtifact} from './create-artifact-dialog-types'

type ArtifactsSidePanelOverlayProps = {
  open: boolean
  onClose: () => void
  tabs: TabItem[]
  activeId: string | null
  activeArtifact: PanelArtifact | null
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLElement | null>) => void
  onActivate: (id: string) => void
  onPromoteOverflowTab: (id: string) => void
  onCloseTab?: (id: string) => void
  returnFocusRef: RefObject<HTMLElement | null>
}

export function ArtifactsSidePanelOverlay({
  open,
  onClose,
  tabs,
  activeId,
  activeArtifact,
  onEdit,
  onActivate,
  onPromoteOverflowTab,
  onCloseTab,
  returnFocusRef,
}: ArtifactsSidePanelOverlayProps) {
  const {selectedVersion, selectVersion} = useArtifactVersionSelection(activeArtifact)

  return (
    <SidePanel
      open={open}
      defaultCloseElement="overlay"
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      aria-label="Artifacts"
      width="var(--artifacts-panel-width, 40%)"
    >
      <div className={styles.Panel}>
        <ArtifactsTabBar
          tabs={tabs}
          activeId={activeId}
          onActivate={onActivate}
          onPromoteOverflowTab={onPromoteOverflowTab}
          onCloseTab={onCloseTab}
          maxVisibleTabs={3}
          trailingActions={
            <>
              <ArtifactHistoryMenu
                key={activeArtifact?.id}
                artifact={activeArtifact}
                selectedVersion={selectedVersion}
                onSelect={selectVersion}
              />
              {onEdit && <ArtifactEditButton artifact={activeArtifact} onEdit={onEdit} />}
              <ArtifactCopyButton artifact={activeArtifact} />
              <IconButton icon={XIcon} variant="invisible" aria-label="Close panel" onClick={onClose} />
            </>
          }
        />
        <div
          className={styles.Content}
          id={activeArtifact ? `artifact-panel-${activeArtifact.id}` : undefined}
          role={activeArtifact ? 'tabpanel' : undefined}
          aria-labelledby={activeArtifact ? `artifact-tab-${activeArtifact.id}` : undefined}
        >
          {activeArtifact && <ArtifactPanelBody artifact={activeArtifact} selectedVersion={selectedVersion} />}
        </div>
      </div>
    </SidePanel>
  )
}
